/**
 * Shop By empty-collection filter.
 *
 * Consumes a precomputed handle → availability map from Liquid metafield
 * bootstrap only (custom.menu_collection_availability, with temporary fallback to
 * app--287098437633 from CM). No client localStorage cache — server embed is
 * source of truth each page load. Never probes per-collection storefront JSON
 * endpoints — that fleet-wide burst was the 429 source.
 *
 * Fail-open: missing map, empty map, or unknown handles leave links visible.
 * Only an explicit `false` hides a collection. After filtering, L1 Shop By tabs
 * whose panels have nothing left to show are hidden too.
 *
 * Perf: unlock as soon as the active tab is filtered; remaining panels run on
 * a short macrotask so first Shop By hover is not blocked by the full walk.
 */
(function () {
  'use strict';

  var LEGACY_CACHE_KEY = 'shop_by_menu_availability';
  var EMBEDDED_ID = 'ShopByMenuAvailability';
  var activeRun = null;
  var didInit = false;

  function isDebugMode() {
    return document.querySelector('[data-empty-debug="true"]') !== null;
  }

  /** Drop pre-metafield localStorage cache so it cannot gut menus again. */
  function clearLegacyClientCache() {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (_) {}
  }

  /**
   * Accepts `{ collections: { handle: bool } }`, a bare map, or null.
   * Empty maps are treated as "no data" so the menu fails open.
   */
  function normalizeCollectionsMap(payload) {
    if (!payload || typeof payload !== 'object') return null;
    var collections =
      payload.collections && typeof payload.collections === 'object'
        ? payload.collections
        : payload;
    if (!collections || typeof collections !== 'object' || Array.isArray(collections)) {
      return null;
    }
    if (payload.empty === true) return null;

    var map = {};
    var count = 0;
    Object.keys(collections).forEach(function (handle) {
      var value = collections[handle];
      if (typeof value !== 'boolean') return;
      map[handle] = value;
      count += 1;
    });
    return count > 0 ? map : null;
  }

  function readEmbeddedPayload() {
    var el = document.getElementById(EMBEDDED_ID);
    if (!el) return { map: null, liquidSource: 'missing-script' };
    var liquidSource = el.getAttribute('data-source') || 'unknown';
    var text = (el.textContent || '').trim();
    if (!text || text === 'null') return { map: null, liquidSource: liquidSource };
    try {
      return { map: normalizeCollectionsMap(JSON.parse(text)), liquidSource: liquidSource };
    } catch (_) {
      return { map: null, liquidSource: liquidSource };
    }
  }

  function summarizeMap(map) {
    if (!map) return { handles: 0, available: 0, empty: 0 };
    var handles = Object.keys(map);
    var available = 0;
    var empty = 0;
    handles.forEach(function (handle) {
      if (map[handle] === true) available += 1;
      else if (map[handle] === false) empty += 1;
    });
    return { handles: handles.length, available: available, empty: empty };
  }

  function collectionLabel(el) {
    var link = el.tagName === 'A' ? el : el.querySelector('a');
    var text = (link && link.textContent) || el.textContent || '';
    return String(text)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 100);
  }

  /** Unique Shop By collection handles currently in the menu DOM (+ panel templates). */
  function collectShopByMenuEntries() {
    var entries = [];
    var seen = {};

    function addFromRoot(root) {
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll('[data-collection-handle]').forEach(function (el) {
        var handle = el.getAttribute('data-collection-handle');
        if (!handle || seen[handle]) return;
        seen[handle] = true;
        entries.push({
          handle: handle,
          label: collectionLabel(el) || handle,
        });
      });
    }

    document
      .querySelectorAll('[data-hide-empty-collections="true"]')
      .forEach(addFromRoot);
    document
      .querySelectorAll('template[data-shopby-panel-template]')
      .forEach(function (tpl) {
        if (tpl.content) addFromRoot(tpl.content);
      });

    return { entries: entries, seen: seen };
  }

  /**
   * Console report: which Shop By items stay visible vs hide by metafield=false.
   * Runs even when CLIENT_HIDE_ENABLED is false (then HIDE = "would hide").
   */
  function logShopByFilterReport(map, hideEnabled) {
    if (typeof console === 'undefined' || !console.info) return;

    var meta = window.__shopByAvailability || {};
    var collected = collectShopByMenuEntries();
    var entries = collected.entries;
    var seen = collected.seen;
    var keep = [];
    var hide = [];

    if (!map) {
      entries.forEach(function (item) {
        keep.push({
          handle: item.handle,
          label: item.label,
          reason: 'no-map-fail-open',
        });
      });
    } else {
      entries.forEach(function (item) {
        if (map[item.handle] === false) {
          hide.push({
            handle: item.handle,
            label: item.label,
            reason: 'metafield=false',
          });
        } else if (map[item.handle] === true) {
          keep.push({
            handle: item.handle,
            label: item.label,
            reason: 'metafield=true',
          });
        } else {
          keep.push({
            handle: item.handle,
            label: item.label,
            reason: 'not-in-map-fail-open',
          });
        }
      });
    }

    var mapFalseNotInMenu = [];
    if (map) {
      Object.keys(map).forEach(function (handle) {
        if (map[handle] === false && !seen[handle]) {
          mapFalseNotInMenu.push(handle);
        }
      });
    }

    var summary = summarizeMap(map);
    function formatEntry(item) {
      return item.label + ' [' + item.handle + ']';
    }

    // One console row only — expand the object in DevTools for lists.
    console.info('[ShopByFilter]', {
      source: meta.source || 'unknown',
      liquidSource: meta.liquidSource || 'unknown',
      clientHideEnabled: Boolean(hideEnabled),
      counts: {
        metafieldHandles: summary.handles,
        metafieldTrue: summary.available,
        metafieldFalse: summary.empty,
        menuHandles: entries.length,
        keepVisible: keep.length,
        hideByMetafield: hide.length,
        mapFalseNotInMenu: mapFalseNotInMenu.length,
      },
      keepVisible: keep.map(formatEntry),
      hideByMetafield: hide.map(formatEntry),
      mapFalseNotInMenu: mapFalseNotInMenu,
      note: hideEnabled
        ? 'Hide ON — hideByMetafield are hidden in the menu.'
        : 'Hide OFF — menu stays visible; hideByMetafield = would be hidden when hide is on.',
    });
  }

  /**
   * Prefer a visible Shop By over a gutting map.
   * All-false / nearly-all-false maps are treated as missing data (fail-open).
   */
  function isMapTooAggressive(map) {
    var summary = summarizeMap(map);
    if (summary.handles === 0) return true;
    if (summary.available === 0) return true;
    if (summary.empty / summary.handles >= 0.95) return true;
    return false;
  }

  function resolveAvailabilityMap() {
    clearLegacyClientCache();

    var embedded = readEmbeddedPayload();
    if (embedded.map) {
      if (isMapTooAggressive(embedded.map)) {
        window.__shopByAvailability = {
          map: null,
          rawMap: embedded.map,
          source: 'embedded-rejected',
          liquidSource: embedded.liquidSource,
        };
        return null;
      }
      window.__shopByAvailability = {
        map: embedded.map,
        source: 'embedded',
        liquidSource: embedded.liquidSource,
      };
      return embedded.map;
    }

    window.__shopByAvailability = {
      map: null,
      source: 'none',
      liquidSource: embedded.liquidSource,
    };
    return null;
  }

  function hasChildMenuItems(el) {
    return el.querySelector('ul li') !== null;
  }

  function applyEmpty(el) {
    if (hasChildMenuItems(el)) return;
    if (isDebugMode()) {
      if (el.querySelector('[data-empty-marker]')) return;
      var link = el.tagName === 'A' ? el : el.querySelector('a');
      if (!link) link = el;
      var span = document.createElement('span');
      span.setAttribute('data-empty-marker', '');
      span.textContent = ' (empty)';
      span.style.color = 'red';
      link.appendChild(span);
    } else {
      el.style.display = 'none';
    }
  }

  function isStillVisibleAfterFilter(li) {
    if (!li.getAttribute('data-collection-handle')) return true;
    if (isDebugMode()) return li.querySelector('[data-empty-marker]') === null;
    return li.style.display !== 'none';
  }

  function hideEmptyShopByColumnsInRoot(root, map) {
    map = map || {};
    root.querySelectorAll('.shopby__col').forEach(function (col) {
      var rows = col.querySelectorAll('.shopby__col-list > li');
      if (rows.length === 0) return;

      var anyVisible = false;
      for (var i = 0; i < rows.length; i++) {
        if (isStillVisibleAfterFilter(rows[i])) {
          anyVisible = true;
          break;
        }
      }
      if (anyVisible) return;

      var parentHandle = col.getAttribute('data-collection-handle');
      if (parentHandle && map[parentHandle] === true) return;

      if (isDebugMode()) {
        var title = col.querySelector('.shopby__col-title');
        if (title && !title.querySelector('[data-empty-column-marker]')) {
          var mark = document.createElement('span');
          mark.setAttribute('data-empty-column-marker', '');
          mark.textContent = ' (column empty)';
          mark.style.color = 'red';
          title.appendChild(mark);
        }
      } else {
        col.style.display = 'none';
      }
    });
  }

  function applyMapToRoot(root, map) {
    if (!map || !root) return;
    root.querySelectorAll('[data-collection-handle]').forEach(function (el) {
      var handle = el.getAttribute('data-collection-handle');
      if (!handle) return;
      if (map[handle] === false) applyEmpty(el);
    });
    hideEmptyShopByColumnsInRoot(root, map);
  }

  /** True when every column is gone / has no remaining visible links. */
  function isShopByPanelVisuallyEmpty(panel) {
    if (!panel) return true;
    var cols = panel.querySelectorAll('.shopby__col');
    if (cols.length === 0) return true;

    for (var i = 0; i < cols.length; i++) {
      var col = cols[i];
      if (!isDebugMode() && col.style.display === 'none') continue;
      if (isDebugMode() && col.querySelector('[data-empty-column-marker]')) continue;

      var rows = col.querySelectorAll('.shopby__col-list > li');
      if (rows.length === 0) return false;

      for (var r = 0; r < rows.length; r++) {
        if (isStillVisibleAfterFilter(rows[r])) return false;
      }
    }
    return true;
  }

  function activateShopByTab(root, tabKey) {
    if (!root || !tabKey) return;
    root.querySelectorAll('[data-shopby-tab]').forEach(function (tab) {
      var on = tab.getAttribute('data-shopby-tab') === tabKey;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-shopby-panel]').forEach(function (panel) {
      var on = panel.getAttribute('data-shopby-panel') === tabKey;
      if (on) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }

  /**
   * Hide L1 Shop By tabs whose panel has nothing left to show after filtering.
   * If the active tab is removed, activate the first remaining tab.
   */
  function hideEmptyShopByTabs(root) {
    if (!root) return;
    var tabs = root.querySelectorAll('[data-shopby-tab]');
    if (tabs.length === 0) return;

    var firstVisibleKey = null;
    var activeKey = null;

    for (var i = 0; i < tabs.length; i++) {
      var tab = tabs[i];
      var key = tab.getAttribute('data-shopby-tab') || '';
      var item = tab.closest('.shopby__tab-item') || tab;
      var panel = key ? root.querySelector('[data-shopby-panel="' + key + '"]') : null;
      var empty = isShopByPanelVisuallyEmpty(panel);

      if (tab.classList.contains('is-active')) activeKey = key;

      if (empty) {
        if (isDebugMode()) {
          if (!tab.querySelector('[data-empty-tab-marker]')) {
            var mark = document.createElement('span');
            mark.setAttribute('data-empty-tab-marker', '');
            mark.textContent = ' (empty tab)';
            mark.style.color = 'red';
            tab.appendChild(mark);
          }
        } else {
          item.style.display = 'none';
        }
      } else {
        item.style.display = '';
        if (!firstVisibleKey) firstVisibleKey = key;
      }
    }

    if (
      !isDebugMode() &&
      firstVisibleKey &&
      activeKey &&
      activeKey !== firstVisibleKey
    ) {
      var activeTab = root.querySelector('[data-shopby-tab="' + activeKey + '"]');
      var activeItem = activeTab && (activeTab.closest('.shopby__tab-item') || activeTab);
      if (activeItem && activeItem.style.display === 'none') {
        activateShopByTab(root, firstVisibleKey);
      }
    }
  }

  function getActivePanel(container) {
    return (
      container.querySelector('.shopby__panel:not([hidden])') ||
      container.querySelector('.shopby__panel')
    );
  }

  function schedulePhase2(fn) {
    // Prefer macrotask over requestIdleCallback. Ads (GPT/Freestar) keep the main
    // thread busy, so idle often fires only when creatives finish — which looks like
    // "AdSense loaded and then Shop By tabs vanished."
    window.setTimeout(fn, 0);
  }

  /**
   * Apply metafield empty-collection map to dormant panels/templates,
   * then hide L1 tabs whose panels are fully empty.
   */
  function applyPhase2Filters(containers, map) {
    if (!map) return;

    for (var j = 0; j < containers.length; j++) {
      var root = containers[j];
      var activePanel = getActivePanel(root);
      if (activePanel) {
        root.querySelectorAll('.shopby__panel').forEach(function (panel) {
          if (panel === activePanel) return;
          applyMapToRoot(panel, map);
        });
      }
      root.querySelectorAll('template[data-shopby-panel-template]').forEach(function (tpl) {
        var panel = tpl.content && tpl.content.querySelector('.shopby__panel');
        if (panel) applyMapToRoot(panel, map);
      });
      hideEmptyShopByTabs(root);
    }
  }

  /**
   * When true, apply metafield empty-collection map (hide handle=false).
   * When false, unlock only and log what would be hidden.
   */
  var CLIENT_HIDE_ENABLED = true;
  var unlockSafetyTimer = null;

  function runFilter() {
    var containers = document.querySelectorAll('[data-hide-empty-collections="true"]');
    if (containers.length === 0) {
      unlockMenu();
      return Promise.resolve();
    }

    var map = resolveAvailabilityMap();
    logShopByFilterReport(map, CLIENT_HIDE_ENABLED);

    if (!CLIENT_HIDE_ENABLED) {
      // Isolation: never gate behind skeleton; only expose availability for logs.
      unlockMenu();
      return Promise.resolve();
    }

    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      var active = getActivePanel(container);
      applyMapToRoot(active || container, map);
    }

    unlockMenu();
    schedulePhase2(function () {
      applyPhase2Filters(containers, map);
    });

    return Promise.resolve();
  }

  function showMenuSkeleton() {
    document.querySelectorAll('[data-shopby]').forEach(function (el) {
      el.classList.add('hidden');
    });
    document.querySelectorAll('[data-shopby-skeleton-loader]').forEach(function (el) {
      el.removeAttribute('hidden');
    });
  }

  function hideMenuSkeleton() {
    document.querySelectorAll('[data-shopby-skeleton-loader]').forEach(function (el) {
      el.setAttribute('hidden', '');
    });
  }

  function unlockMenu() {
    if (unlockSafetyTimer) {
      clearTimeout(unlockSafetyTimer);
      unlockSafetyTimer = null;
    }
    document.body.classList.remove('menu-filtering');
    document.querySelectorAll('[data-shopby]').forEach(function (el) {
      el.classList.remove('hidden');
    });
    hideMenuSkeleton();
    window.dispatchEvent(new CustomEvent('mega-menu:ready'));
  }

  function init() {
    if (document.querySelectorAll('[data-hide-empty-collections="true"]').length === 0) return;
    if (activeRun || didInit) return;
    didInit = true;

    // Never leave Shop By stuck on skeleton if filter JS stalls.
    unlockSafetyTimer = window.setTimeout(function () {
      unlockMenu();
    }, 1500);

    if (!CLIENT_HIDE_ENABLED) {
      var mapForLog = resolveAvailabilityMap();
      logShopByFilterReport(mapForLog, false);
      unlockMenu();
      return;
    }

    // Gate only while client hide is actually applying the map.
    showMenuSkeleton();

    activeRun = runFilter();
    activeRun
      .catch(function () {
        unlockMenu();
      })
      .finally(function () {
        activeRun = null;
      });
  }

  function isShopByTarget(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        [
          '[data-shopby]',
          '[data-shopby-skeleton-loader]',
          '.mega-menu__layout--shopby',
          '.shop-option-menu',
          '#HeaderDrawer-shopby',
          '#Details-menu-drawer-shopby',
        ].join(', ')
      )
    );
  }

  /** If the user opens Shop By before defer init finishes, prioritize unlock. */
  function onEarlyShopByIntent(event) {
    if (!isShopByTarget(event.target)) return;
    init();
  }

  // defer: runs after HTML parse (HP can paint), still before typical first hover.
  init();

  document.addEventListener('pointerenter', onEarlyShopByIntent, true);
  document.addEventListener('focusin', onEarlyShopByIntent, true);

  window.addEventListener('pagereveal', function (e) {
    if (e.viewTransition) {
      didInit = false;
      init();
    }
  });
  // Do not re-run on section-morphed while isolating Shop By vanish vs ads.
})();
